/**
 * ================================================================================
 * PÁGINA: Super Admin Dashboard
 * PROPÓSITO: Visão geral do sistema para o Super Administrador
 * ================================================================================
 */
import React, { useEffect, useState } from 'react';
import { useFinance } from '../../context/FinanceContext';
import { Users, ArrowLeftRight, ShieldAlert, Shield } from 'lucide-react';
import api from '../../utils/api';
import { Navigate } from 'react-router-dom';

export default function SuperAdminDashboard() {
  const { user } = useFinance();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Busca as stats se o usuário for super admin
    if (user && user.is_super_admin) {
      api.get('/admin/stats')
        .then(res => {
          setStats(res.data);
          setLoading(false);
        })
        .catch(err => {
          setError('Erro ao carregar dados administrativos.');
          setLoading(false);
        });
    }
  }, [user]);

  // Redireciona caso não seja super admin, como segurança extra no frontend
  if (user && !user.is_super_admin) {
    return <Navigate to="/" replace />;
  }

  if (loading) {
    return <div className="p-6">A carregar painel de administração...</div>;
  }

  if (error) {
    return <div className="p-6 text-red-500">{error}</div>;
  }

  return (
    <div className="page-container animate-in">
      <header className="page-header">
        <h1 className="page-title" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Shield style={{ color: 'var(--accent-blue)' }} size={28} />
          Painel Super Admin
        </h1>
        <p className="page-subtitle">Visão panorâmica da plataforma KumbuOs</p>
      </header>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 20, marginBottom: 32 }}>
        <div className="card" style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, color: 'var(--text-secondary)' }}>
            <div style={{ padding: 10, background: 'var(--accent-blue-dim)', borderRadius: 'var(--radius-md)' }}>
              <Users size={20} style={{ color: 'var(--accent-blue)' }} />
            </div>
            <span style={{ fontWeight: 600 }}>Total de Utilizadores</span>
          </div>
          <div style={{ fontSize: 36, fontWeight: 800, fontFamily: 'var(--font-mono)' }}>
            {stats.total_users}
          </div>
        </div>

        <div className="card" style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, color: 'var(--text-secondary)' }}>
            <div style={{ padding: 10, background: 'var(--accent-green-dim)', borderRadius: 'var(--radius-md)' }}>
              <ArrowLeftRight size={20} style={{ color: 'var(--accent-green)' }} />
            </div>
            <span style={{ fontWeight: 600 }}>Transações Registadas</span>
          </div>
          <div style={{ fontSize: 36, fontWeight: 800, fontFamily: 'var(--font-mono)' }}>
            {stats.total_transactions}
          </div>
        </div>
      </div>

      <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 16 }}>Últimos Utilizadores Registados</h2>
      <div className="card" style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', textAlign: 'left', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: 'var(--surface-color-alt)', borderBottom: '1px solid var(--border-color)', fontSize: 13, color: 'var(--text-secondary)' }}>
              <th style={{ padding: '12px 20px', fontWeight: 600 }}>Nome</th>
              <th style={{ padding: '12px 20px', fontWeight: 600 }}>Email</th>
              <th style={{ padding: '12px 20px', fontWeight: 600 }}>Telegram</th>
              <th style={{ padding: '12px 20px', fontWeight: 600 }}>Data Registo</th>
            </tr>
          </thead>
          <tbody>
            {stats && stats.recent_users && stats.recent_users.map(u => (
              <tr key={u.id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                <td style={{ padding: '16px 20px', fontWeight: 500 }}>{u.name}</td>
                <td style={{ padding: '16px 20px', color: 'var(--text-secondary)' }}>{u.email}</td>
                <td style={{ padding: '16px 20px', color: 'var(--text-secondary)' }}>
                  {u.telegram_chat_id || '-'}
                </td>
                <td style={{ padding: '16px 20px', color: 'var(--text-secondary)' }}>
                  {new Date(u.created_at).toLocaleDateString('pt-AO')}
                </td>
              </tr>
            ))}
            {(!stats || !stats.recent_users || stats.recent_users.length === 0) && (
              <tr>
                <td colSpan="4" style={{ padding: 32, textAlign: 'center', color: 'var(--text-secondary)' }}>
                  Nenhum utilizador registado ainda.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
